import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Table, Button, Badge, Spinner, Alert } from 'react-bootstrap'
import client from '../../api/client'
import CreatorForm from './CreatorForm'

function CreatorCodes() {
  const [creators, setCreators] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingCreator, setEditingCreator] = useState(null)
  const [formLoading, setFormLoading] = useState(false)

  useEffect(() => {
    fetchCreators()
  }, [])

  const fetchCreators = async () => {
    setLoading(true)
    try {
      const response = await client.get('/admin/api/v1/influencers')
      setCreators(response.data.influencers || response.data || [])
    } catch (err) {
      console.error('Error fetching creators:', err)
      setError('Erreur lors du chargement des créateurs')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingCreator(null)
    setShowForm(true)
  }

  const handleEdit = (creator) => {
    setEditingCreator(creator)
    setShowForm(true)
  }

  const handleDelete = async (creator) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir désactiver le créateur "${creator.code}" ?`)) {
      return
    }

    try {
      await client.delete(`/admin/api/v1/influencers/${creator.id}`)
      setSuccess('Créateur désactivé avec succès')
      fetchCreators()
    } catch (err) {
      console.error('Error deleting creator:', err)
      setError('Erreur lors de la désactivation du créateur')
    }
  }

  const handleFormSubmit = async (formData) => {
    setFormLoading(true)
    try {
      if (editingCreator) {
        await client.patch(`/admin/api/v1/influencers/${editingCreator.id}`, formData)
        setSuccess('Créateur mis à jour avec succès')
      } else {
        await client.post('/admin/api/v1/influencers', formData)
        setSuccess('Créateur ajouté avec succès')
      }
      setShowForm(false)
      fetchCreators()
    } catch (err) {
      console.error('Error saving creator:', err)
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement du créateur')
    } finally {
      setFormLoading(false)
    }
  }

  const getTierBadge = (tier) => {
    const colors = {
      'F1': 'secondary',
      'F2': 'info',
      'F3': 'warning',
      'F4': 'primary',
    }
    return <Badge bg={colors[tier] || 'secondary'}>{tier}</Badge>
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0)
  }

  // Calculate totals
  const totalEarnings = creators.reduce((sum, c) => sum + (c.total_earnings || 0), 0)
  const totalSales = creators.reduce((sum, c) => sum + (c.total_sales || 0), 0)
  const pendingPayouts = creators.reduce((sum, c) => sum + (c.pending_payout || 0), 0)

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h1 className="h3">Codes créateurs</h1>
          <p className="text-muted">
            Gérez les créateurs et leurs commissions
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleCreate}>
            + Ajouter un créateur
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title className="h2 text-primary">{creators.length}</Card.Title>
              <Card.Text className="text-muted">Créateurs actifs</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title className="h2 text-success">{formatCurrency(totalEarnings)}</Card.Title>
              <Card.Text className="text-muted">Total des gains</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title className="h2 text-warning">{formatCurrency(pendingPayouts)}</Card.Title>
              <Card.Text className="text-muted">Paiements en attente</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : creators.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>Aucun créateur</p>
              <Button variant="primary" onClick={handleCreate}>
                Ajouter un créateur
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Tier</th>
                  <th>Commission</th>
                  <th>Ventes</th>
                  <th>Gains totaux</th>
                  <th>En attente</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {creators.map((creator) => (
                  <tr key={creator.id}>
                    <td>
                      <strong>{creator.code}</strong>
                      {creator.description && (
                        <div className="small text-muted">{creator.description}</div>
                      )}
                      {creator.user_email && (
                        <div className="small text-muted">{creator.user_email}</div>
                      )}
                    </td>
                    <td>{getTierBadge(creator.tier)}</td>
                    <td>{creator.commission_rate || 10}%</td>
                    <td>{(creator.total_sales || 0).toLocaleString()}</td>
                    <td className="text-success">{formatCurrency(creator.total_earnings)}</td>
                    <td className="text-warning">{formatCurrency(creator.pending_payout)}</td>
                    <td>
                      {creator.active !== false ? (
                        <Badge bg="success">Actif</Badge>
                      ) : (
                        <Badge bg="secondary">Inactif</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(creator)}
                      >
                        Modifier
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(creator)}
                      >
                        Désactiver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="table-secondary">
                  <th colSpan={3}>Total</th>
                  <th>{totalSales.toLocaleString()}</th>
                  <th className="text-success">{formatCurrency(totalEarnings)}</th>
                  <th className="text-warning">{formatCurrency(pendingPayouts)}</th>
                  <th colSpan={2}></th>
                </tr>
              </tfoot>
            </Table>
          )}
        </Card.Body>
      </Card>

      <CreatorForm
        show={showForm}
        onHide={() => setShowForm(false)}
        onSubmit={handleFormSubmit}
        creator={editingCreator}
        loading={formLoading}
      />
    </Container>
  )
}

export default CreatorCodes
